import {
  isTrackReference,
  isTrackReferencePinned,
} from "@livekit/components-core";
import {
  AudioTrack,
  ConnectionQualityIndicator,
  LockLockedIcon,
  ParticipantContextIfNeeded,
  ParticipantName,
  type ParticipantTileProps,
  ScreenShareIcon,
  TrackRefContextIfNeeded,
  VideoTrack,
  useEnsureTrackRef,
  useFeatureContext,
  useIsEncrypted,
  useIsMuted,
  useMaybeLayoutContext,
  useMaybeRoomContext,
  useParticipantTile,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import React, { forwardRef, useCallback, useMemo } from "react";
import colors from "../../../app/colors";
import {
  MicOffOutlinedIcon,
  PushPinFilledIcon,
  PushPinOutlinedIcon,
  ScreenShareOutlinedIcon,
} from "./video-call-bar-icons";
import { VideoCallParticipantPlaceholderAvatar } from "./video-call-participant-placeholder";
import { useVideoCallPinControls } from "./video-call-pin-controls";

export type VideoCallParticipantTileProps = ParticipantTileProps & {
  /** Clicking anywhere on the tile pins it to the stage (used for the side strip). */
  focusOnClick?: boolean;
  /** Minimal rendering for tiny surfaces (PiP overlay): no name, gradient or pin button. */
  compact?: boolean;
};

/**
 * LiveKit {@link ParticipantTile} with app-specific metadata: participant name without the
 * bottom pill background, a red outlined mic-off icon when muted, a pin/unpin control, and
 * a "you're sharing" card in place of the local screen share video.
 */
export const VideoCallParticipantTile = forwardRef<
  HTMLDivElement,
  VideoCallParticipantTileProps
>(
  (
    {
      trackRef,
      children,
      onParticipantClick,
      disableSpeakingIndicator,
      focusOnClick = false,
      compact = false,
      ...htmlProps
    },
    ref,
  ) => {
    const trackReference = useEnsureTrackRef(trackRef);
    const layoutContext = useMaybeLayoutContext();
    const pinControls = useVideoCallPinControls();
    const room = useMaybeRoomContext();

    const isPinned = layoutContext
      ? isTrackReferencePinned(trackReference, layoutContext.pin.state)
      : false;
    const canPin = Boolean(layoutContext && pinControls) && !compact;
    const isScreenShare = trackReference.source === Track.Source.ScreenShare;
    const isLocalScreenShare =
      isScreenShare && trackReference.participant.isLocal;

    const handleTileClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        htmlProps.onClick?.(event);
        if (focusOnClick && !isPinned) {
          pinControls?.pinTrack(trackReference);
        }
      },
      [focusOnClick, htmlProps, isPinned, pinControls, trackReference],
    );

    const { elementProps } = useParticipantTile<HTMLDivElement>({
      htmlProps: {
        ...htmlProps,
        onClick: handleTileClick,
        className: [
          htmlProps.className,
          focusOnClick && !isPinned ? "video-call-tile-clickable" : undefined,
          compact ? "video-call-tile-compact" : undefined,
        ]
          .filter(Boolean)
          .join(" "),
      },
      disableSpeakingIndicator,
      onParticipantClick,
      trackRef: trackReference,
    });
    const isEncrypted = useIsEncrypted(trackReference.participant);
    const autoManageSubscription = useFeatureContext()?.autoSubscription;

    const handleSubscribe = useCallback(
      (subscribed: boolean) => {
        if (
          trackReference.source &&
          !subscribed &&
          layoutContext &&
          layoutContext.pin.dispatch &&
          isTrackReferencePinned(trackReference, layoutContext.pin.state)
        ) {
          layoutContext.pin.dispatch({ msg: "clear_pin" });
        }
      },
      [trackReference, layoutContext],
    );

    const togglePin = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (!pinControls) return;
        if (isPinned) {
          pinControls.unpin();
        } else {
          pinControls.pinTrack(trackReference);
        }
      },
      [isPinned, pinControls, trackReference],
    );

    const stopLocalScreenShare = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        room?.localParticipant.setScreenShareEnabled(false).catch(() => {
          // LiveKit already logs; nothing further to do.
        });
      },
      [room],
    );

    const micTrackRef = useMemo(
      () => ({
        participant: trackReference.participant,
        source: Track.Source.Microphone,
        publication: trackReference.participant.getTrackPublication(
          Track.Source.Microphone,
        ),
      }),
      [trackReference.participant],
    );
    const isMicMuted = useIsMuted(micTrackRef);

    let media: React.ReactNode = null;
    if (isLocalScreenShare) {
      media = (
        <div className="video-call-local-share-card">
          <ScreenShareOutlinedIcon size={compact ? 24 : 40} />
          <span className="video-call-local-share-card-title">
            You&apos;re sharing your screen
          </span>
          {!compact && (
            <button
              type="button"
              className="video-call-local-share-card-stop"
              onClick={stopLocalScreenShare}
            >
              Stop sharing
            </button>
          )}
        </div>
      );
    } else if (
      isTrackReference(trackReference) &&
      (trackReference.publication?.kind === "video" ||
        trackReference.source === Track.Source.Camera ||
        isScreenShare)
    ) {
      media = (
        <VideoTrack
          trackRef={trackReference}
          onSubscriptionStatusChanged={handleSubscribe}
          manageSubscription={autoManageSubscription}
        />
      );
    } else if (isTrackReference(trackReference)) {
      media = (
        <AudioTrack
          trackRef={trackReference}
          onSubscriptionStatusChanged={handleSubscribe}
        />
      );
    }

    return (
      <div ref={ref} style={{ position: "relative" }} {...elementProps}>
        <TrackRefContextIfNeeded trackRef={trackReference}>
          <ParticipantContextIfNeeded participant={trackReference.participant}>
            {children ?? (
              <>
                {media}
                {!isLocalScreenShare && (
                  <div className="lk-participant-placeholder video-call-participant-placeholder">
                    <VideoCallParticipantPlaceholderAvatar
                      participant={trackReference.participant}
                    />
                  </div>
                )}
                {!compact && !isLocalScreenShare && (
                  <>
                    <div
                      aria-hidden
                      className="video-call-participant-name-gradient"
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: "5.5rem",
                        pointerEvents: "none",
                        background:
                          "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.70) 67.11%)",
                      }}
                    />
                    <div className="lk-participant-metadata">
                      <div className="video-call-participant-metadata-primary">
                        {!isScreenShare ? (
                          <>
                            {isEncrypted && (
                              <LockLockedIcon
                                style={{
                                  width: 20,
                                  height: 20,
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            {isMicMuted && (
                              <MicOffOutlinedIcon
                                size={20}
                                aria-hidden
                                style={{ color: colors.red, flexShrink: 0 }}
                              />
                            )}
                            <ParticipantName className="video-call-participant-name" />
                          </>
                        ) : (
                          <>
                            <ScreenShareIcon
                              style={{ width: 20, height: 20, flexShrink: 0 }}
                            />
                            <ParticipantName className="video-call-participant-name">
                              &apos;s screen
                            </ParticipantName>
                          </>
                        )}
                      </div>
                      <ConnectionQualityIndicator className="lk-participant-metadata-item" />
                    </div>
                  </>
                )}
                {canPin && (
                  <button
                    type="button"
                    className={`video-call-pin-button${
                      isPinned ? " is-pinned" : ""
                    }`}
                    aria-label={isPinned ? "Unpin from stage" : "Pin to stage"}
                    aria-pressed={isPinned}
                    title={isPinned ? "Unpin" : "Pin"}
                    onClick={togglePin}
                  >
                    {isPinned ? (
                      <PushPinFilledIcon size={18} />
                    ) : (
                      <PushPinOutlinedIcon size={18} />
                    )}
                  </button>
                )}
              </>
            )}
          </ParticipantContextIfNeeded>
        </TrackRefContextIfNeeded>
      </div>
    );
  },
);
